import { Component, OnInit, ViewChild } from '@angular/core';
import { Client } from 'core/models/client';
import { ClientsService } from 'core/services/clients.service';
import { Location } from '@angular/common';
import { DynamicFormComponent } from 'core/components/forms/dynamic-form.component';
import { StudentsInviteQuestionsService } from './students-invite-questions.service';
import { ActivatedRoute } from '@angular/router';
import { SubscriptionsService } from 'core/services/subscriptions.service';
import { TranslateService } from '@ngx-translate/core';
import { PopUpService } from 'core/services';

@Component({
  selector: 'lms-students-invite',
  templateUrl: './students-invite.component.html',
  styleUrls: ['./students-invite.component.scss'],
  providers: [StudentsInviteQuestionsService]
})
export class StudentsInviteComponent implements OnInit {
  @ViewChild('clientForm', { static: false }) public clientForm: DynamicFormComponent;
  @ViewChild('selfRegistrationForm', { static: false })
  public selfRegistrationForm: DynamicFormComponent;

  public client: Client;
  public questions: any[];
  public clientQuestions: any[];
  public data;

  private seatsAvailable: number;
  private canInviteStudents: boolean;
  private creditsClient: boolean;

  constructor(
    public activatedRoute: ActivatedRoute,
    public clientsService: ClientsService,
    public location: Location,
    public questionsService: StudentsInviteQuestionsService,
    public subscriptionsService: SubscriptionsService,
    public popupService: PopUpService,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    this.activatedRoute.data.subscribe((data) => {
      this.client = data['client'];
      this.setQuestions();
    });
  }

  setQuestions() {
    if (this.client) {
      this.data = {
        client: this.client
      };
      this.questions = this.questionsService.getQuestions(this.data);
    } else {
      this.clientQuestions = this.questionsService.getClientQuestions(this.client);
    }
  }

  updateSelfRegistrationConfig(selfRegistration: any) {
    selfRegistration.validDomains = selfRegistration.validDomains ? selfRegistration.validDomains.join() : undefined;
    const updatedClient: Client = Object.assign({}, this.client, { selfRegistration });

    this.clientsService
      .update(updatedClient)
      .then((client) => {
        this.client = client;
        this.endSubmit(null);
        this.setQuestions();
      })
      .catch((err) => {
        this.endSubmit(err);
      });
  }

  selectClient(data) {
    const clientId = data.clientId;
    if (clientId) {
      this.clientsService.getClient(data.clientId).subscribe((client) => {
        this.canInvite(client).then((canInvite) => {
          if (canInvite) {
            this.client = client;
            this.clientForm.submitted(null);
            this.setQuestions();
          } else {
            this.showError();
          }
        });
      });
    }
  }

  endSubmit(value) {
    this.selfRegistrationForm.submitted(value);
  }

  cancel() {
    this.location.back();
  }

  /**
   * Checks if a client has an active and valid subscription with seats available
   * @param client
   */
  async canInvite(client: Client): Promise<boolean> {
    let canInvite: boolean;

    if (client.subscriptions.users) {
      await this.subscriptionsService.getClientSubscription(client.id).then((subscription) => {
        if (!subscription) {
          canInvite = false;
        } else {
          this.seatsAvailable = subscription.rateLimit - subscription.rateConsumed;
          this.canInviteStudents = !!this.seatsAvailable && new Date(subscription.endDate).getTime() - Date.now() > 0;
          canInvite = this.canInviteStudents;
        }
      });
    } else {
      this.creditsClient = true;
      canInvite = false;
    }
    return canInvite;
  }

  showError() {
    const description = this.creditsClient
      ? 'students.subscriptionCheck.creditsClient'
      : this.seatsAvailable === 0
      ? 'students.subscriptionCheck.noSeats'
      : 'students.subscriptionCheck.noActiveSubscription';
    this.popupService
      .showError({
        title: this.translate.instant('students.subscriptionCheck.cantInviteStudents'),
        text: this.translate.instant(description)
      })
      .then(() => {
        this.location.back();
      });
  }

  goBack(): void {
    this.location.back();
  }
}
